import {
  closestCenter,
  DndContext,
  DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  horizontalListSortingStrategy,
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import React, { ReactElement, useMemo, useState } from "react";
import {
  restrictToFirstScrollableAncestor,
  restrictToHorizontalAxis,
  restrictToVerticalAxis,
} from "@dnd-kit/modifiers";
import { SHSortableItem, SHSortableItemProps } from "../sh-sortable-item";
import { SHStack } from "@components/design-systems";

type SHSortableContextProps<T> = {
  items: T[];
  itemProps?: SHSortableItemProps;
  direction?: "horizontal" | "vertical";
  reOrderOnDragEnd?: boolean;
  getItemId: (item: T) => string;
  renderItem?: (item: T) => JSX.Element;
  renderSortableItem?: (
    item: T,
    index: number,
  ) => ReactElement<SHSortableItemProps>;
  onDragEnd?: (event: DragEndEvent, items: T[]) => void;
};

export default function SHSortableContext<T>({
  items,
  itemProps,
  direction = "vertical",
  reOrderOnDragEnd = false,
  getItemId,
  renderItem,
  renderSortableItem,
  onDragEnd,
}: SHSortableContextProps<T>) {
  const sensors = useSensors(useSensor(PointerSensor));

  const [contextItemIds, setContextItemIds] = useState<string[]>(() =>
    items.map(getItemId),
  );

  const itemMap = useMemo(
    () =>
      items.reduce((acc, item) => {
        acc[getItemId(item)] = item;

        return acc;
      }, {} as Record<string, T>),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [items],
  );

  useMemo(() => {
    setContextItemIds(items.map(getItemId));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items]);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!active?.id || !over?.id) return;

    if (active?.id !== over?.id) {
      const oldIndex = contextItemIds.findIndex((item) => item === active.id);
      const newIndex = contextItemIds.findIndex((item) => item === over.id);

      const newItemIds = arrayMove(contextItemIds, oldIndex, newIndex);
      setContextItemIds(newItemIds);

      let newItems = newItemIds.map((id) => itemMap[id] as T);
      if (reOrderOnDragEnd) {
        newItems = newItems.map((item, index) => {
          if (!item || typeof item !== "object" || !("order" in item)) {
            return item;
          }

          return { ...item, order: index + 1 };
        });
      }

      onDragEnd?.(event, newItems);
    }
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
      modifiers={[
        direction === "horizontal"
          ? restrictToHorizontalAxis
          : restrictToVerticalAxis,
        restrictToFirstScrollableAncestor,
      ]}
    >
      <SortableContext
        items={contextItemIds}
        strategy={
          direction === "horizontal"
            ? horizontalListSortingStrategy
            : verticalListSortingStrategy
        }
      >
        {contextItemIds?.map((id, index) => {
          const item = itemMap[id];

          return renderSortableItem ? (
            <SHStack key={id}>{renderSortableItem(item, index)}</SHStack>
          ) : (
            <SHSortableItem id={id} itemId={id} key={id} {...itemProps}>
              {renderItem ? renderItem(item) : id}
            </SHSortableItem>
          );
        })}
      </SortableContext>
    </DndContext>
  );
}
